import React, { useState, useEffect } from 'react';

const Dosificador = () =>
{
  // Estados para los inputs
  const [materialType, setMaterialType] = useState('concrete');
  const [volume, setVolume] = useState(1.00);
  const [strength, setStrength] = useState(210);
  const [aggregateSize, setAggregateSize] = useState(20);
  const [slump, setSlump] = useState(10);
  const [morteroType, setMorteroType] = useState('1:4');
  
  // Precios (puedes modificar estos valores)
  const [prices, setPrices] = useState({
    cement: 0.15,    // $ por kg
    sand: 0.050,       // $ por kg
    gravel: 0.10,     // $ por kg
    water: 0.003      // $ por litro
  });

  const [results, setResults] = useState(null);

  // Constantes para los cálculos
  const strengthToWCRatio = {
    140: 0.8,
    175: 0.7,
    210: 0.62,
    245: 0.56,
    280: 0.48,
    350: 0.43,
    420: 0.38
  };

  const morteroRatios = {
    '1:2': { cement: 1, sand: 2 },
    '1:3': { cement: 1, sand: 3 },
    '1:4': { cement: 1, sand: 4 },
    '1:5': { cement: 1, sand: 5 },
    '1:6': { cement: 1, sand: 6 }
  };

  // Efecto para calcular en tiempo real
  useEffect(() => {
    if (volume <= 0) {
      setResults(null);
      return;
    }

    if (materialType === 'concrete') {
      // Cálculos para concreto
      const wcRatio = strengthToWCRatio[strength] || 0.5;
      const cement = (volume * 1500) / (1 + wcRatio + 2.5 + 1.5);
      const water = cement * wcRatio;
      const fineAggregate = cement * 2.5;
      const coarseAggregate = cement * 1.5;
      
      // Cálculo de costos
      const cementCost = cement * prices.cement;
      const waterCost = water * prices.water;
      const sandCost = fineAggregate * prices.sand;
      const gravelCost = coarseAggregate * prices.gravel;
      const totalCost = cementCost + waterCost + sandCost + gravelCost;
      
      setResults({
        type: 'concreto',
        cement: cement.toFixed(2),
        water: water.toFixed(2),
        fineAggregate: fineAggregate.toFixed(2),
        coarseAggregate: coarseAggregate.toFixed(2),
        wcRatio: wcRatio.toFixed(2),
        costs: {
          cement: cementCost.toFixed(2),
          water: waterCost.toFixed(2),
          sand: sandCost.toFixed(2),
          gravel: gravelCost.toFixed(2),
          total: totalCost.toFixed(2)
        }
      });
    } else {
      // Cálculos para mortero
      const [cementPart, sandPart] = morteroType.split(':').map(Number);
      const totalParts = cementPart + sandPart;
      
      const cement = (volume * 2200 * cementPart) / totalParts;
      const sand = (volume * 2200 * sandPart) / totalParts;
      const water = cement * 0.25;
      
      // Cálculo de costos
      const cementCost = cement * prices.cement;
      const waterCost = water * prices.water;
      const sandCost = sand * prices.sand;
      const totalCost = cementCost + waterCost + sandCost;
      
      setResults({
        type: 'mortero',
        cement: cement.toFixed(2),
        sand: sand.toFixed(2),
        water: water.toFixed(2),
        ratio: morteroType,
        costs: {
          cement: cementCost.toFixed(2),
          water: waterCost.toFixed(2),
          sand: sandCost.toFixed(2),
          total: totalCost.toFixed(2)
        }
      });
    }
  }, [materialType, volume, strength, aggregateSize, slump, morteroType, prices]);

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPrices(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Calculadora de Dosificación y Costos</h2>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="materialType" className="block text-sm font-medium text-gray-700">
            Tipo de material
          </label>
          <select
            id="materialType"
            value={materialType}
            onChange={(e) => setMaterialType(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          >
            <option value="concrete">Concreto</option>
            <option value="mortero">Mortero</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="volume" className="block text-sm font-medium text-gray-700">
            Volumen ({materialType === 'concrete' ? 'm³' : 'm³'})
          </label>
          <input
            type="number"
            id="volume"
            min="0.01"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value) || 0)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
          />
        </div>
        
        {materialType === 'concrete' ? (
          <>
            <div>
              <label htmlFor="strength" className="block text-sm font-medium text-gray-700">
                Resistencia a la compresión (kg/cm²)
              </label>
              <select
                id="strength"
                value={strength}
                onChange={(e) => setStrength(parseInt(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              >
                <option value="140">140 kg/cm²</option>
                <option value="175">175 kg/cm²</option>
                <option value="210">210 kg/cm²</option>
                <option value="245">245 kg/cm²</option>
                <option value="280">280 kg/cm²</option>
                <option value="350">350 kg/cm²</option>
                <option value="420">420 kg/cm²</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="aggregateSize" className="block text-sm font-medium text-gray-700">
                Tamaño máximo del agregado (mm)
              </label>
              <select
                id="aggregateSize"
                value={aggregateSize}
                onChange={(e) => setAggregateSize(parseInt(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              >
                <option value="10">10 mm</option>
                <option value="20">20 mm</option>
                <option value="40">40 mm</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="slump" className="block text-sm font-medium text-gray-700">
                Asentamiento (slump) (cm)
              </label>
              <select
                id="slump"
                value={slump}
                onChange={(e) => setSlump(parseInt(e.target.value))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              >
                <option value="5">5 cm</option>
                <option value="10">10 cm</option>
                <option value="15">15 cm</option>
              </select>
            </div>
          </>
        ) : (
          <div>
            <label htmlFor="morteroType" className="block text-sm font-medium text-gray-700">
              Proporción de mortero (cemento:arena)
            </label>
            <select
              id="morteroType"
              value={morteroType}
              onChange={(e) => setMorteroType(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
            >
              <option value="1:2">1:2 (Para acabados finos)</option>
              <option value="1:3">1:3 (Repellos y tarrajeos)</option>
              <option value="1:4">1:4 (Mampostería)</option>
              <option value="1:5">1:5 (Asentado de ladrillos)</option>
              <option value="1:6">1:6 (Cimientos)</option>
            </select>
          </div>
        )}

        {/* Sección de precios */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Precios unitarios</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="cementPrice" className="block text-sm font-medium text-gray-700">
                Cemento ($/kg)
              </label>
              <input
                type="number"
                id="cementPrice"
                name="cement"
                min="0"
                step="0.01"
                value={prices.cement}
                onChange={handlePriceChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              />
            </div>
            <div>
              <label htmlFor="sandPrice" className="block text-sm font-medium text-gray-700">
                Arena ($/kg)
              </label>
              <input
                type="number"
                id="sandPrice"
                name="sand"
                min="0"
                step="0.01"
                value={prices.sand}
                onChange={handlePriceChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              />
            </div>
            {materialType === 'concrete' && (
              <div>
                <label htmlFor="gravelPrice" className="block text-sm font-medium text-gray-700">
                  Grava ($/kg)
                </label>
                <input
                  type="number"
                  id="gravelPrice"
                  name="gravel"
                  min="0"
                  step="0.01"
                  value={prices.gravel}
                  onChange={handlePriceChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                />
              </div>
            )}
            <div>
              <label htmlFor="waterPrice" className="block text-sm font-medium text-gray-700">
                Agua ($/litro)
              </label>
              <input
                type="number"
                id="waterPrice"
                name="water"
                min="0"
                step="0.001"
                value={prices.water}
                onChange={handlePriceChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              />
            </div>
          </div>
        </div>
      </div>
      
      {results && volume > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Resultados para {volume} {materialType === 'concrete' ? 'm³ de concreto' : 'm³ de mortero'}:
          </h3>
          
          {results.type === 'concreto' ? (
            <>
              <ul className="space-y-2 mb-4">
                <li className="flex justify-between">
                  <span className="font-medium">Cemento:</span>
                  <span>{results.cement} kg (${results.costs.cement})</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Agua:</span>
                  <span>{results.water} litros (${results.costs.water})</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Agregado fino (arena):</span>
                  <span>{results.fineAggregate} kg (${results.costs.sand})</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Agregado grueso (grava):</span>
                  <span>{results.coarseAggregate} kg (${results.costs.gravel})</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Relación agua/cemento:</span>
                  <span>{results.wcRatio}</span>
                </li>
              </ul>
              <div className="border-t pt-3 mt-3">
                <p className="flex justify-between font-bold text-lg">
                  <span>Costo total:</span>
                  <span>${results.costs.total}</span>
                </p>
              </div>
            </>
          ) : (
            <>
              <ul className="space-y-2 mb-4">
                <li className="flex justify-between">
                  <span className="font-medium">Cemento:</span>
                  <span>{results.cement} kg (${results.costs.cement})</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Arena:</span>
                  <span>{results.sand} kg (${results.costs.sand})</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Agua:</span>
                  <span>{results.water} litros (${results.costs.water})</span>
                </li>
                <li className="flex justify-between">
                  <span className="font-medium">Proporción:</span>
                  <span>{results.ratio} (cemento:arena)</span>
                </li>
              </ul>
              <div className="border-t pt-3 mt-3">
                <p className="flex justify-between font-bold text-lg">
                  <span>Costo total:</span>
                  <span>${results.costs.total}</span>
                </p>
              </div>
            </>
          )}
          
          <div className="mt-4 text-sm text-gray-600">
            <p className="font-medium">Nota:</p>
            <p>
              {results.type === 'concreto' 
                ? 'Esta es una estimación basada en el método ACI. Para proyectos reales, se recomienda realizar pruebas de laboratorio.'
                : 'Las proporciones de agua pueden variar según la humedad de la arena. Ajuste según necesidad.'}
            </p>
            <p className="mt-1">Los costos no incluyen mano de obra, transporte ni otros gastos adicionales.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dosificador;